import { MeetingProvider } from "@videosdk.live/react-sdk";
import { useEffect, useRef, useState, useCallback, Component } from "react";
import { MeetingAppProvider } from "../../../hooks/MeetingAppContextDef";
import { MeetingContainer } from "../meeting/MeetingContainer";
import { LeaveScreen } from "../screens/LeaveScreen";
import { JoiningScreen } from "../screens/JoiningScreen";
import CameraCalibrationScreen from "../screens/CameraCalibrationScreen";
import EndInterviewOverlay from "../meeting/components/EndInterviewOverlay";
import { encryptToken } from "../../../utils/decryptURL";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  setSocketCloseSuppressed,
  reconnectInterviewSocket,
  isEOIReceived,
  isLSTReceived,
} from "../../../hooks/socketManager";

class NetworkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    const msg = error?.message?.toLowerCase() || "";
    const isNet =
      msg.includes("network") ||
      msg.includes("fetch") ||
      msg.includes("load failed") ||
      msg.includes("offline") ||
      msg.includes("connection") ||
      msg.includes("websocket") ||
      msg.includes("connecting") ||
      msg.includes("invalidstateerror");
    return isNet ? { hasError: true } : null;
  }
  componentDidCatch(error, errorInfo) {
    const msg = error?.message?.toLowerCase() || "";
    const isNet =
      msg.includes("network") ||
      msg.includes("fetch") ||
      msg.includes("load failed") ||
      msg.includes("offline") ||
      msg.includes("connection");
    if (!isNet) console.error("Non-network error caught:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) return this.props.fallback || null;
    return this.props.children;
  }
}

/* ─── RECONNECTING BANNER ───────────────────────────────────────────────────
   Floats over the meeting as a non-blocking toast for 15 s while we wait.
   The meeting stays fully mounted underneath.
──────────────────────────────────────────────────────────────────────────── */
const ReconnectingBanner = ({ secondsLeft }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 9998,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(10,13,26,0.6)",
      backdropFilter: "blur(4px)", // optional premium feel
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        minWidth: 360,
        maxWidth: 520,
        background: "#111729",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 20,
        padding: "20px 24px",
        boxShadow: "0 10px 50px rgba(0,0,0,0.6)",
        color: "#fff",
      }}
    >
      {/* Spinner */}
      <div
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: 12,
          background: "rgba(251,191,36,0.12)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          style={{
            width: 22,
            height: 22,
            color: "#FBBF24",
            animation: "spin 1s linear infinite",
          }}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
          <circle
            style={{ opacity: 0.25 }}
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            style={{ opacity: 0.85 }}
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          className="app-textbox"
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 700,
            color: "#fff",
            lineHeight: 1.4,
          }}
        >
          Connection lost — reconnecting…
        </p>
        <p
          style={{
            margin: "6px 0 0",
            fontSize: 13,
            color: "rgba(255,255,255,0.6)",
          }}
        >
          Your session is being held. Please wait.
        </p>
      </div>

      {/* Countdown */}
      <div
        style={{
          flexShrink: 0,
          width: 42,
          height: 42,
          borderRadius: "50%",
          background: "rgba(251,191,36,0.12)",
          border: "2px solid rgba(251,191,36,0.35)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "monospace",
          fontSize: 14,
          fontWeight: 700,
          color: "#FBBF24",
        }}
      >
        {secondsLeft}s
      </div>
    </div>
  </div>
);

/* ─── OFFLINE SCREEN ────────────────────────────────────────────────────────
   Full blocking overlay shown after 15 s with no recovery.
──────────────────────────────────────────────────────────────────────────── */
const OfflineScreen = ({ reason = "offline", onReconnect }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: "rgba(10,13,26,0.97)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
    }}
  >
    <div
      style={{
        background: "#111729",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 20,
        padding: "40px 36px",
        maxWidth: 520,
        width: "100%",
        textAlign: "center",
      }}
    >
      {/* Icon */}
      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "rgba(239,68,68,0.12)",
            border: "1px solid rgba(239,68,68,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            style={{ width: 26, height: 26, color: "#F87171" }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M18.364 5.636a9 9 0 010 12.728M15.536 8.464a5 5 0 010 7.072M12 12h.01M3 3l18 18"
            />
          </svg>
        </div>
      </div>

      {/* Heading */}
      <h2
        className="app-modal-heading"
        style={{
          margin: "0 0 10px",

          fontWeight: 700,
          color: "#fff",
        }}
      >
        {reason === "unstable"
          ? "Your network is too unstable"
          : "You lost your internet connection"}
      </h2>

      {/* Body */}
      <p
        style={{
          margin: "0 0 6px",
          fontSize: 13,
          color: "rgba(255,255,255,0.55)",
          lineHeight: 1.6,
        }}
      >
        Your assessment session is being held.
      </p>
      <p
        className="app-body "
        style={{
          margin: "0 0 28px",

          color: "rgba(255,255,255,0.8)",
          fontWeight: 500,
          lineHeight: 1.6,
        }}
      >
        Once your network is back, your assessment will resume automatically. If
        it doesn't resume, tap <em>Reconnect</em> below. Please avoid refreshing
        the page. If you refresh, you will need to log in again, but you will
        still be able to continue from where you left off.
      </p>

      {/* Reconnect button */}
      <button
        className="app-button"
        onClick={onReconnect}
        style={{
          width: "100%",
          padding: "13px 0",
          borderRadius: 12,
          background: "#fff",
          color: "#0f172a",
          fontWeight: 700,

          border: "none",
          cursor: "pointer",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        Reconnect
      </button>

      {/* Pulse */}
      <div
        style={{
          marginTop: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          fontSize: 11,
          color: "rgba(255,255,255,0.3)",
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "#EF4444",
            display: "inline-block",
            animation: "pulse 1.5s ease-in-out infinite",
          }}
        />
        <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
        Waiting for internet connection…
      </div>
    </div>
  </div>
);

/* ─── TECHNICAL ISSUE SCREEN ────────────────────────────────────────────────
   Shown when the interview socket closes UNEXPECTEDLY — i.e. before an EOI
   (end-of-interview) message ever arrived. This is deliberately lighter than
   OfflineScreen: it doesn't poll for connectivity or auto-redirect, because
   the network itself may be fine (only the socket dropped). The candidate
   stays on this screen until they explicitly tap Continue.
──────────────────────────────────────────────────────────────────────────── */
const TechnicalIssueScreen = ({ onContinue }) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: "rgba(10,13,26,0.97)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
    }}
  >
    <div
      style={{
        background: "#111729",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 20,
        padding: "40px 36px",
        maxWidth: 520,
        width: "100%",
        textAlign: "center",
      }}
    >
      {/* Icon */}
      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "rgba(251,191,36,0.12)",
            border: "1px solid rgba(251,191,36,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            style={{ width: 26, height: 26, color: "#FBBF24" }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zM12 15.75h.007v.008H12v-.008z"
            />
          </svg>
        </div>
      </div>

      {/* Heading */}
      <h2
        className="app-modal-heading"
        style={{ margin: "0 0 10px", fontWeight: 700, color: "#fff" }}
      >
        We hit a small technical issue
      </h2>

      {/* Body */}
      <p
        style={{
          margin: "0 0 6px",
          fontSize: 13,
          color: "rgba(255,255,255,0.55)",
          lineHeight: 1.6,
        }}
      >
        Don't worry — nothing is lost.
      </p>
      <p
        className="app-body "
        style={{
          margin: "0 0 28px",
          color: "rgba(255,255,255,0.8)",
          fontWeight: 500,
          lineHeight: 1.6,
        }}
      >
        Your progress has been saved. Tap <em>Continue</em> below to pick up
        your assessment right where you left off.
      </p>

      {/* Continue button */}
      <button
        className="app-button"
        onClick={onContinue}
        style={{
          width: "100%",
          padding: "13px 0",
          borderRadius: 12,
          background: "#fff",
          color: "#0f172a",
          fontWeight: 700,
          border: "none",
          cursor: "pointer",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
      >
        Continue
      </button>
    </div>
  </div>
);

/* ─── CAMERA BLOCKED SCREEN ─────────────────────────────────────────────────
   Full blocking overlay shown when camera permission is denied/revoked during
   an active assessment. Polls every 1.5 s; as soon as permission is granted
   it auto-redirects to /connection-restored so the normal resume flow runs.
──────────────────────────────────────────────────────────────────────────── */
const CameraBlockedScreen = () => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: "rgba(10,13,26,0.97)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
    }}
  >
    <div
      style={{
        background: "#111729",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 20,
        padding: "40px 36px",
        maxWidth: 520,
        width: "100%",
        textAlign: "center",
      }}
    >
      {/* Icon */}
      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "rgba(239,68,68,0.12)",
            border: "1px solid rgba(239,68,68,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            style={{ width: 26, height: 26, color: "#F87171" }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 10l4.553-2.277A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2V8z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
          </svg>
        </div>
      </div>

      {/* Heading */}
      <h2
        className="app-modal-heading"
        style={{ margin: "0 0 10px", fontWeight: 700, color: "#fff" }}
      >
        Camera access blocked
      </h2>

      {/* Body */}
      <p
        style={{
          margin: "0 0 6px",
          fontSize: 13,
          color: "rgba(255,255,255,0.55)",
          lineHeight: 1.6,
        }}
      >
        Your assessment has been paused.
      </p>
      <p
        className="app-body"
        style={{
          margin: "0 0 28px",
          color: "rgba(255,255,255,0.8)",
          fontWeight: 500,
          lineHeight: 1.6,
        }}
      >
        Please enable camera permission in your browser settings, then return to
        this tab. Your session will resume automatically once access is
        restored.
      </p>

      {/* Pulse — no manual button needed, polls automatically */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          fontSize: 11,
          color: "rgba(255,255,255,0.3)",
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "#EF4444",
            display: "inline-block",
            animation: "pulse-cam 1.5s ease-in-out infinite",
          }}
        />
        <style>{`@keyframes pulse-cam{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
        Waiting for camera permission…
      </div>
    </div>
  </div>
);

/* ─── WINDOW MANAGEMENT BLOCKED SCREEN ──────────────────────────────────────
   Full blocking overlay shown when Window Management permission is revoked
   during an active assessment. Polls every 1.5 s; auto-redirects to
   /connection-restored as soon as permission is re-granted.
──────────────────────────────────────────────────────────────────────────── */
const WindowMgmtBlockedScreen = () => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: "rgba(10,13,26,0.97)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
    }}
  >
    <div
      style={{
        background: "#111729",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 20,
        padding: "40px 36px",
        maxWidth: 520,
        width: "100%",
        textAlign: "center",
      }}
    >
      {/* Icon */}
      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "rgba(239,68,68,0.12)",
            border: "1px solid rgba(239,68,68,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            style={{ width: 26, height: 26, color: "#F87171" }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7"
            />
          </svg>
        </div>
      </div>

      {/* Heading */}
      <h2
        className="app-modal-heading"
        style={{ margin: "0 0 10px", fontWeight: 700, color: "#fff" }}
      >
        Window Management access blocked
      </h2>

      {/* Body */}
      <p
        style={{
          margin: "0 0 6px",
          fontSize: 13,
          color: "rgba(255,255,255,0.55)",
          lineHeight: 1.6,
        }}
      >
        Your assessment has been paused.
      </p>
      <p
        className="app-body"
        style={{
          margin: "0 0 28px",
          color: "rgba(255,255,255,0.8)",
          fontWeight: 500,
          lineHeight: 1.6,
        }}
      >
        Please re-enable <strong>Window Management</strong> permission in your
        browser site settings (click the lock/info icon in the address bar →
        Window Management → Allow), then return to this tab. Your session will
        resume automatically.
      </p>

      {/* Pulse — no manual button needed, polls automatically */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          fontSize: 11,
          color: "rgba(255,255,255,0.3)",
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "#EF4444",
            display: "inline-block",
            animation: "pulse-wm 1.5s ease-in-out infinite",
          }}
        />
        <style>{`@keyframes pulse-wm{0%,100%{opacity:1}50%{opacity:.3}}`}</style>
        Waiting for Window Management permission…
      </div>
    </div>
  </div>
);

// ─── Tuneable constants ────────────────────────────────────────────────────────
const OFFLINE_GRACE_MS = 15000;
const UNSTABLE_LATENCY_THRESHOLD_MS = 10000;
const UNSTABLE_CONSECUTIVE_THRESHOLD = 3;
// ──────────────────────────────────────────────────────────────────────────────

function Interview() {
  /* ─── Meeting state ─────────────────────────────────────────── */

  const [token, setToken] = useState("");
  const [meetingId, setMeetingId] = useState("");
  const [participantName, setParticipantName] = useState("");
  const [meetingKey, setMeetingKey] = useState(0);
  const [micOn, setMicOn] = useState(false);
  const [webcamOn, setWebcamOn] = useState(false);
  const [customAudioStream, setCustomAudioStream] = useState(null);
  const [customVideoStream, setCustomVideoStream] = useState(null);
  const [isMeetingStarted, setMeetingStarted] = useState(false);
  const [isMeetingLeft, setIsMeetingLeft] = useState(false);
  const [showEndInterview, setShowEndInterview] = useState(false);
  const [showOfflineToast, setShowOfflineToast] = useState(false);
  // Shown when the interview socket closes BEFORE an EOI message arrived —
  // i.e. an unexpected/technical drop rather than the natural end of the
  // interview. See onSocketClose below.
  const [showTechnicalIssue, setShowTechnicalIssue] = useState(false);
  const [showAlreadyCompletedToast, setShowAlreadyCompletedToast] =
    useState(false);
  const [isCameraSetupDone, setIsCameraSetupDone] = useState(false);
  const [isCameraBlocked, setIsCameraBlocked] = useState(false);
  const [isWindowMgmtBlocked, setIsWindowMgmtBlocked] = useState(false);
  const location = useLocation();
  const [params] = useSearchParams();
  const redirectFrom =
    location.state?.redirectFrom || params.get("redirectFrom") || "allSet";
  console.log("Redirected from:", redirectFrom);

  /* ─── Network UI state ──────────────────────────────────────────
     "online"       → normal
     "reconnecting" → reconnecting banner shown, meeting stays alive
     "offline"      → full blocking offline screen
  ─────────────────────────────────────────────────────────────── */
  const [networkStatus, setNetworkStatus] = useState("online");
  const [offlineReason, setOfflineReason] = useState("offline");
  const [reconnectSecondsLeft, setReconnectSecondsLeft] = useState(
    OFFLINE_GRACE_MS / 1000,
  );
  const ignoreSocketClosureRef = useRef(false);

  /* ─── THE FIX: a single "network brain" ref ─────────────────────
     All mutable network logic lives here. Because it's a ref:
     - It never goes stale — always reads current values via closures
       over the ref itself, not over React state.
     - No useCallback dependency arrays to get wrong.
     - Browser event listeners point to stable functions that read
       from this ref, so they never need to be re-registered.
  ─────────────────────────────────────────────────────────────── */
  const net = useRef({
    // current status mirror (avoids async lag of React state)
    status: "online",

    // timers
    graceTimer: null,
    countdownInterval: null,
    pollInterval: null,

    // unstable counter
    badPings: 0,

    // set by useEffect below so the brain can reach React setters
    setNetworkStatus: null,
    setOfflineReason: null,
    setReconnectSecondsLeft: null,
    getCustomAudioStream: null,
    getCustomVideoStream: null,
    getPingUrl: null,
  });

  useEffect(() => {
    const onSocketClose = (e) => {
      if (ignoreSocketClosureRef.current) {
        console.log(
          "[Socket] Closed during interview cleanup => ignoring reconnect flow",
        );
        return;
      }

      // socketManager tells us (via event detail) whether EOI/LST had
      // already arrived when the socket closed. Fall back to the live
      // getters in case some other caller dispatches this event without a
      // detail.
      const eoiAlreadyArrived = e?.detail?.eoiReceived ?? isEOIReceived();
      const lstAlreadyArrived = e?.detail?.lstReceived ?? isLSTReceived();

      if (eoiAlreadyArrived || lstAlreadyArrived) {
        // Interview already reached its natural end (or LST — the last
        // stretch before it, which can hold EOI back for a while if a
        // whiteboard round is open) — endInterviewSafely / EndInterviewOverlay
        // owns this transition from here. A close in this window is
        // expected, not a technical drop, even if EOI itself hasn't been
        // locally processed yet (e.g. it's still sitting in the whiteboard
        // hold queue). Stay on the current screen.
        console.log(
          "[Socket] Closed after LST/EOI — expected end of interview, ignoring",
        );
        return;
      }

      if (sessionStorage.getItem("intentionalLeave") === "true") {
        console.log("[Socket] Closed intentionally via Leave Assessment, ignoring");
        return;
      }

      console.warn("[Socket] Closed before LST/EOI → technical issue flow");
      triggerTechnicalIssue();
    };

    const onSocketError = () => {
      if (ignoreSocketClosureRef.current) {
        console.log(
          "[Socket] Error during interview cleanup => ignoring reconnect flow",
        );
        return;
      }

      if (isEOIReceived() || isLSTReceived()) {
        console.log("[Socket] Error after LST/EOI — expected, ignoring");
        return;
      }

      if (sessionStorage.getItem("intentionalLeave") === "true") {
        console.log("[Socket] Error intentionally via Leave Assessment, ignoring");
        return;
      }

      console.warn("[Socket] Error → triggering reconnect flow");
      triggerOffline("offline");
    };

    const onDisableGuard = () => {
      ignoreSocketClosureRef.current = true;
    };

    // CMPLTD from the interview socket means this interview session was
    // already completed (e.g. finished in another tab/session). Instead of
    // treating it as offline/reconnect, show a banner and hand off straight
    // to the EndInterviewOverlay.
    const onAlreadyCompleted = () => {
      ignoreSocketClosureRef.current = true;
      setSocketCloseSuppressed(true);
      setShowAlreadyCompletedToast(true);

      setTimeout(() => {
        setShowAlreadyCompletedToast(false);
        setShowEndInterview(true);
      }, 5000);
    };

    window.addEventListener("interview-socket-closed", onSocketClose);
    window.addEventListener("interview-socket-error", onSocketError);
    window.addEventListener("disable-interview-guard", onDisableGuard);
    window.addEventListener("interview-already-completed", onAlreadyCompleted);

    return () => {
      window.removeEventListener("interview-socket-closed", onSocketClose);
      window.removeEventListener("interview-socket-error", onSocketError);
      window.removeEventListener(
        "interview-already-completed",
        onAlreadyCompleted,
      );
      window.removeEventListener("disable-interview-guard", onDisableGuard);
    };
  }, []);
  // Wire React setters into the brain after first render
  useEffect(() => {
    net.current.setNetworkStatus = setNetworkStatus;
    net.current.setOfflineReason = setOfflineReason;
    net.current.setReconnectSecondsLeft = setReconnectSecondsLeft;
  }, []); // intentionally empty — setters are stable

  // Keep live values available to the brain via getters
  useEffect(() => {
    net.current.getCustomAudioStream = () => customAudioStream;
    net.current.getCustomVideoStream = () => customVideoStream;
  }, [customAudioStream, customVideoStream]);

  /* ─── Brain helpers (all defined on net.current so they're always fresh) ── */

  // checkConnection — pings the server, returns true/false
  const checkConnection = async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      const res = await fetch("https://www.google.com/favicon.ico", {
        method: "HEAD",
        mode: "no-cors",
        signal: controller.signal,
      });

      clearTimeout(timeout);
      return true;
    } catch {
      return false;
    }
  };
  // stopAllMedia — stops tracks and dispatches events
  const stopAllMedia = () => {
    try {
      net.current
        .getCustomAudioStream()
        ?.getTracks()
        .forEach((t) => {
          t.stop();
          t.enabled = false;
        });
      net.current
        .getCustomVideoStream()
        ?.getTracks()
        .forEach((t) => {
          t.stop();
          t.enabled = false;
        });
      window.dispatchEvent(new CustomEvent("force-stop-recording"));
      window.dispatchEvent(new CustomEvent("force-stop-all-media"));
      window.dispatchEvent(new CustomEvent("disable-user-interactions"));
    } catch (e) {
      console.error("stopAllMedia error:", e);
    }
  };
  const hasDeadTracks = (stream) => {
    if (!stream) return true;
    return stream.getTracks().some((t) => t.readyState !== "live");
  };
  const refreshMediaIfNeeded = async () => {
    const audio = net.current.getCustomAudioStream?.();
    const video = net.current.getCustomVideoStream?.();

    const dead = hasDeadTracks(audio) || hasDeadTracks(video);

    if (!dead) {
      console.log("[Media] Tracks are healthy — no refresh needed");
      return false;
    }

    console.warn("[Media] Dead tracks detected → refreshing getUserMedia");

    try {
      const freshStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      const newAudio = new MediaStream(freshStream.getAudioTracks());
      const newVideo = new MediaStream(freshStream.getVideoTracks());

      setCustomAudioStream(newAudio);
      setCustomVideoStream(newVideo);

      return true; // indicates refresh happened
    } catch (e) {
      console.error("[Media] Failed to refresh media:", e);
      return false;
    }
  };
  // clearGraceTimers — cancels the 15 s countdown
  const clearGraceTimers = () => {
    if (net.current.graceTimer) {
      clearTimeout(net.current.graceTimer);
      net.current.graceTimer = null;
    }
    if (net.current.countdownInterval) {
      clearInterval(net.current.countdownInterval);
      net.current.countdownInterval = null;
    }
    net.current.badPings = 0;
  };

  // setStatus — single place to update both ref and React state
  const setStatus = (status) => {
    net.current.status = status;
    net.current.setNetworkStatus?.(status);
  };

  // commitOffline — escalates to the full offline screen
  const commitOffline = (reason) => {
    if (net.current.status === "offline") return;

    console.warn("[Network] commitOffline:", reason);
    clearGraceTimers();
    // Grace period is over and we're truly offline — re-enable socket events
    // so that future socket closes (after the user reconnects) are handled.
    setSocketCloseSuppressed(false);
    net.current.setOfflineReason?.(reason);
    setStatus("offline");
    sessionStorage.setItem("interviewOffline", "true");
    stopAllMedia();

    if (net.current.pollInterval) clearInterval(net.current.pollInterval);
    net.current.pollInterval = setInterval(async () => {
      const ok = await checkConnection();
      if (ok) {
        clearInterval(net.current.pollInterval);
        sessionStorage.removeItem("interviewOffline");
        window.location.replace("/connection-restored");
      }
    }, 1500);
  };

  // triggerTechnicalIssue — socket dropped before EOI arrived. Unlike
  // triggerOffline/commitOffline this does NOT assume the network is down:
  // it doesn't poll connectivity or auto-redirect. It just pauses the
  // candidate on a reassuring banner until they choose to continue.
  const triggerTechnicalIssue = () => {
    if (net.current.status === "offline" || showTechnicalIssue) return;

    console.warn(
      "[Network] Socket closed before EOI — showing technical issue screen",
    );

    // Guard against a flurry of duplicate close/error events while the
    // banner is up (mirrors how commitOffline/triggerOffline behave).
    setSocketCloseSuppressed(true);
    stopAllMedia();
    sessionStorage.setItem("interviewTechnicalIssue", "true");
    setShowTechnicalIssue(true);
  };

  // handleTechnicalIssueContinue — user tapped Continue on the technical
  // issue screen. Hand off to the same /connection-restored resume flow
  // used for network/camera/window-management recovery, just with its own
  // "technical" messaging.
  const handleTechnicalIssueContinue = () => {
    setShowTechnicalIssue(false);
    setSocketCloseSuppressed(false);
    sessionStorage.removeItem("interviewTechnicalIssue");
    sessionStorage.setItem("restoredFrom", "technical");
    window.location.replace("/connection-restored");
  };

  // triggerOffline — starts 15 s grace period, shows reconnecting banner
  const triggerOffline = (reason = "offline") => {
    // Only start the grace period if we're currently "online"
    if (net.current.status !== "online") return;

    console.warn(
      `[Network] triggerOffline (${reason}) — going offline immediately`,
    );

    // Suppress socket close/error events so a server-side close during the
    // grace period doesn't re-trigger the offline flow a second time.
    // setSocketCloseSuppressed(true);

    // FIX: Notify the recording hook that a reconnect is starting so it does
    // not wipe session_id/timestamps while uploads drain during the grace period.
    // window.dispatchEvent(new CustomEvent("interview-reconnect-start"));

    // net.current.setOfflineReason?.(reason);
    // net.current.setReconnectSecondsLeft?.(OFFLINE_GRACE_MS / 1000);
    // setStatus("reconnecting");

    // // Countdown ticker
    // net.current.countdownInterval = setInterval(() => {
    //   net.current.setReconnectSecondsLeft?.((s) => Math.max(0, s - 1));
    // }, 1000);

    // // Grace timer — after 15 s, check once more then commit if still down
    // net.current.graceTimer = setTimeout(async () => {
    //   net.current.graceTimer = null;

    //   if (net.current.countdownInterval) {
    //     clearInterval(net.current.countdownInterval);
    //     net.current.countdownInterval = null;
    //   }

    //   console.warn("[Network] Grace period elapsed — final check…");

    //   const ok = await checkConnection();

    //   if (ok) {
    //     console.log("[Network] Recovered at last moment — going back online");
    //     // Re-enable socket events and reconnect the interview socket.
    //     setSocketCloseSuppressed(false);
    //     reconnectInterviewSocket();
    //     // FIX: Release the session_id guard — reconnect succeeded within grace period.
    //     window.dispatchEvent(new CustomEvent("interview-reconnect-end"));
    //     setStatus("online");
    //     return;
    //   }

    commitOffline(reason);
    // }, OFFLINE_GRACE_MS);
  };

  // triggerOnline — handles browser "online" event
  const triggerOnline = async () => {
    console.log(`[Network] triggerOnline — status: "${net.current.status}"`);

    // CASE 1: We were reconnecting (within grace period)
    if (net.current.status === "reconnecting") {
      const ok = await checkConnection();

      if (ok) {
        console.log("[Network] Recovered before grace ended — fixing media...");

        clearGraceTimers();

        // Re-enable socket events now that we're recovering
        setSocketCloseSuppressed(false);

        //  STEP 1: Refresh media if needed
        const refreshed = await refreshMediaIfNeeded();

        //  STEP 2: Force meeting re-init if media was refreshed
        if (refreshed) {
          console.warn("[Fix] Resetting meeting due to dead tracks");

          // STEP 1: Stop meeting FIRST
          setMeetingStarted(false);

          // STEP 2: small delay (important) — also dispatch reconnect-end AFTER
          // the meeting restarts so startQARecording (triggered by bot-started-speaking)
          // runs with the guard already released and a fresh session_id already set.
          setTimeout(() => {
            setMeetingKey((prev) => prev + 1);

            // STEP 3: restart meeting with fresh tracks
            setMeetingStarted(true);

            // STEP 4: Reconnect interview socket — it was closed by the server
            // during the network drop and must be re-established.
            reconnectInterviewSocket();

            // FIX: Release the session_id guard now that the meeting is back up.
            window.dispatchEvent(new CustomEvent("interview-reconnect-end"));
          }, 300);
        } else {
          // No media refresh needed — but socket may still be dead, reconnect it.
          reconnectInterviewSocket();
          // Release the guard immediately.
          window.dispatchEvent(new CustomEvent("interview-reconnect-end"));
        }

        setStatus("online");
      }

      return;
    }

    // CASE 2: Already in offline screen
    if (
      net.current.status === "offline" &&
      sessionStorage.getItem("interviewOffline") === "true"
    ) {
      if (net.current.pollInterval) clearInterval(net.current.pollInterval);

      const ok = await checkConnection();

      if (ok) {
        console.log("[Network] Fully recovered from offline");

        // 🔥 Refresh media before redirect
        await refreshMediaIfNeeded();

        sessionStorage.removeItem("interviewOffline");
        window.location.replace("/connection-restored");
      } else {
        net.current.pollInterval = setInterval(async () => {
          const isOnline = await checkConnection();

          if (isOnline) {
            clearInterval(net.current.pollInterval);

            console.log("[Network] Recovered via polling");

            await refreshMediaIfNeeded();

            sessionStorage.removeItem("interviewOffline");
            window.location.replace("/connection-restored");
          }
        }, 1500);
      }
    }
  };
  useEffect(() => {
    const interval = setInterval(async () => {
      if (
        net.current.status === "online" ||
        net.current.status === "reconnecting"
      ) {
        const ok = await checkConnection();
        if (!ok) {
          console.warn("[Network] Silent drop detected");
          triggerOffline("offline");
        }
      }
    }, 3000);

    return () => clearInterval(interval);
  }, []);
  /* ─── Register event listeners ONCE, pointing at stable functions ── */
  // triggerOffline/triggerOnline read from net.current, never go stale.
  // The browser "offline" event passes an Event object as the first arg —
  // we explicitly pass the string "offline" instead.
  useEffect(() => {
    const onOffline = () => triggerOffline("offline");
    const onOnline = () => triggerOnline();

    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);

    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
      clearGraceTimers();
      if (net.current.pollInterval) clearInterval(net.current.pollInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // empty — triggerOffline/triggerOnline only use net.current

  /* ─── Page-refresh guard ────────────────────────────────────── */
  useEffect(() => {
    const refreshed = sessionStorage.getItem("pageRefreshed");
    if (refreshed === "true") {
      sessionStorage.removeItem("pageRefreshed");
      window.location.replace("/screenup-login");
    }
  }, []);

  /* ─── Auth guard ────────────────────────────────────────────── */
  useEffect(() => {
    if (!sessionStorage.getItem("interviewAuthToken"))
      window.location.href = "/screenup-login";
  }, []);

  /* ─── Suppress React error overlay while not online ────────── */
  useEffect(() => {
    const style = document.createElement("style");
    style.id = "hide-error-overlay";
    document.head.appendChild(style);
    const tick = () => {
      style.textContent =
        net.current.status !== "online"
          ? `iframe[style*="position: fixed"][style*="z-index"]{display:none!important}[data-overlay]{display:none!important}`
          : "";
    };
    tick();
    const id = setInterval(tick, 100);
    return () => {
      clearInterval(id);
      style.remove();
    };
  }, []);

  /* ─── Unstable polling (while meeting is active) ────────────── */
  useEffect(() => {
    if (!isMeetingStarted) return;

    net.current.badPings = 0;

    const checkUnstable = async () => {
      if (net.current.status !== "online") return;

      const start = performance.now();
      let bad = false;
      try {
        const ctrl = new AbortController();
        const t = setTimeout(
          () => ctrl.abort(),
          UNSTABLE_LATENCY_THRESHOLD_MS + 1000,
        );

        clearTimeout(t);
        bad = performance.now() - start > UNSTABLE_LATENCY_THRESHOLD_MS;
      } catch {
        bad = true;
      }

      if (bad) {
        net.current.badPings += 1;
        console.warn(
          `[Network] Bad ping #${net.current.badPings}/${UNSTABLE_CONSECUTIVE_THRESHOLD}`,
        );
        if (net.current.badPings >= UNSTABLE_CONSECUTIVE_THRESHOLD) {
          triggerOffline("unstable");
        }
      } else {
        if (net.current.badPings > 0)
          console.log(
            `[Network] Good ping — resetting counter (was ${net.current.badPings})`,
          );
        net.current.badPings = 0;
      }
    };

    const id = setInterval(checkUnstable, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeetingStarted]); // triggerOffline uses net.current — no dep needed

  /* ─── Safari visibility fallback ───────────────────────────── */
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== "visible") return;
      if (net.current.status !== "offline") return;
      if (sessionStorage.getItem("interviewOffline") !== "true") return;
      const ok = await checkConnection();
      if (ok) {
        sessionStorage.removeItem("interviewOffline");
        window.location.replace("/connection-restored");
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Stale offline flag on mount ──────────────────────────── */
  useEffect(() => {
    (async () => {
      if (sessionStorage.getItem("interviewOffline") === "true") {
        const ok = await checkConnection();
        if (ok) sessionStorage.removeItem("interviewOffline");
        else {
          net.current.status = "offline";
          setNetworkStatus("offline");
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Stale technical-issue flag on mount ──────────────────────
     If the page reloaded while the technical-issue banner was up (candidate
     refreshed instead of tapping Continue), re-show it rather than silently
     dropping them into a fresh joining screen with a socket that never
     reached EOI.
  ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (sessionStorage.getItem("interviewTechnicalIssue") === "true") {
      setSocketCloseSuppressed(true);
      setShowTechnicalIssue(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReconnectButton = useCallback(async () => {
    const ok = await checkConnection();

    if (ok) {
      sessionStorage.removeItem("interviewOffline");
      window.location.replace("/connection-restored");
    } else {
      console.warn("[Reconnect] Still offline");

      setShowOfflineToast(true);

      setTimeout(() => {
        setShowOfflineToast(false);
      }, 3000);
    }
  }, []);

  /* ─── Bot queue ─────────────────────────────────────────────── */
  const waitForBotToFinish = () =>
    new Promise((resolve) => {
      const check = () => {
        const s = window.getBotQueueStatus?.();
        if (!s || s.isBusy === false) return resolve();
        setTimeout(check, 300);
      };
      check();
    });

  const endInterviewSafely = async () => {
    // At end of interview we may get a socket close from the server;
    // avoid showing reconnect state when that is expected.
    ignoreSocketClosureRef.current = true;

    await waitForBotToFinish();
    setShowEndInterview(true);
  };

  /* ─── Camera permission monitor (active only during meeting) ───────────────
     Checks every 1.5 s while the meeting is running. If permission is revoked:
       1. Stops all media + sockets (same as going offline)
       2. Shows CameraBlockedScreen (full blocking overlay)
     Once permission is restored it auto-redirects to /connection-restored.
  ─────────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!isMeetingStarted) return;

    const checkCamera = async () => {
      try {
        if (navigator.permissions) {
          const status = await navigator.permissions.query({ name: "camera" });
          if (status.state === "denied") {
            if (!isCameraBlocked) {
              // Stop everything — same teardown as going offline
              stopAllMedia();
              setSocketCloseSuppressed(false);
              sessionStorage.setItem("cameraBlocked", "true");
            }
            setIsCameraBlocked(true);
          } else if (status.state === "granted") {
            if (
              isCameraBlocked ||
              sessionStorage.getItem("cameraBlocked") === "true"
            ) {
              // Camera restored — follow the same path as network recovery
              sessionStorage.removeItem("cameraBlocked");
              sessionStorage.setItem("restoredFrom", "camera");
              window.location.replace("/connection-restored");
            }
            setIsCameraBlocked(false);
          }
        } else {
          // Fallback: try getUserMedia
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: true,
            });
            stream.getTracks().forEach((t) => t.stop());
            if (
              isCameraBlocked ||
              sessionStorage.getItem("cameraBlocked") === "true"
            ) {
              sessionStorage.removeItem("cameraBlocked");
              sessionStorage.setItem("restoredFrom", "camera");
              window.location.replace("/connection-restored");
            }
            setIsCameraBlocked(false);
          } catch {
            if (!isCameraBlocked) {
              stopAllMedia();
              setSocketCloseSuppressed(false);
              sessionStorage.setItem("cameraBlocked", "true");
            }
            setIsCameraBlocked(true);
          }
        }
      } catch {
        // Permissions API not available — silently ignore
      }
    };

    checkCamera(); // immediate check on mount / meeting start
    const cameraInterval = setInterval(checkCamera, 1500);
    return () => clearInterval(cameraInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeetingStarted, isCameraBlocked]);

  /* ─── Window Management permission monitor (active only during meeting) ────
     Uses navigator.permissions.query for "window-management". If permission is
     revoked mid-assessment:
       1. Stops all media + sockets (same teardown as camera block)
       2. Shows WindowMgmtBlockedScreen (full blocking overlay)
     Once permission is restored it auto-redirects to /connection-restored so
     the normal resume flow runs — identical to the camera recovery path.
  ─────────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!isMeetingStarted) return;

    let permStatus = null;

    const checkWindowMgmt = async () => {
      try {
        if (!navigator.permissions) return; // API unavailable — skip silently

        // Reuse cached PermissionStatus object to avoid repeated queries
        if (!permStatus) {
          try {
            permStatus = await navigator.permissions.query({
              name: "window-management",
            });
          } catch {
            // "window-management" not supported in this browser — skip silently
            return;
          }
        }

        if (permStatus.state === "denied") {
          if (!isWindowMgmtBlocked) {
            stopAllMedia();
            setSocketCloseSuppressed(false);
            sessionStorage.setItem("windowMgmtBlocked", "true");
          }
          setIsWindowMgmtBlocked(true);
        } else if (permStatus.state === "granted") {
          if (
            isWindowMgmtBlocked ||
            sessionStorage.getItem("windowMgmtBlocked") === "true"
          ) {
            sessionStorage.removeItem("windowMgmtBlocked");
            sessionStorage.setItem("restoredFrom", "windowMgmt");
            window.location.replace("/connection-restored");
          }
          setIsWindowMgmtBlocked(false);
        }
      } catch {
        // Permissions API threw unexpectedly — silently ignore
      }
    };

    checkWindowMgmt(); // immediate check on mount / meeting start
    const wmInterval = setInterval(checkWindowMgmt, 1500);
    return () => clearInterval(wmInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeetingStarted, isWindowMgmtBlocked]);

  /* ─── Render ────────────────────────────────────────────────── */
  const isOffline = networkStatus === "offline";
  // const isReconnecting = networkStatus === "reconnecting";

  return (
    <NetworkErrorBoundary
      fallback={
        <OfflineScreen
          reason={offlineReason}
          onReconnect={handleReconnectButton}
        />
      }
    >
      <MeetingAppProvider>
        {showEndInterview ? (
          <EndInterviewOverlay />
        ) : showTechnicalIssue ? (
          <TechnicalIssueScreen onContinue={handleTechnicalIssueContinue} />
        ) : isCameraBlocked ? (
          <CameraBlockedScreen />
        ) : isWindowMgmtBlocked ? (
          <WindowMgmtBlockedScreen />
        ) : isOffline ? (
          <OfflineScreen
            reason={offlineReason}
            onReconnect={handleReconnectButton}
          />
        ) : (
          <>
            {/* Reconnecting banner: Uncomment for 15second display */}
            {/* {isReconnecting && !showEndInterview && (
              <ReconnectingBanner secondsLeft={reconnectSecondsLeft} />
            )} */}

            {isMeetingStarted ? (
              <MeetingProvider
                key={meetingKey}
                token={token}
                joinWithoutUserInteraction
                reinitialiseMeetingOnConfigChange
                config={{
                  meetingId,
                  micEnabled: micOn,
                  webcamEnabled: webcamOn,
                  name: participantName || "Bot",
                  multiStream: true,
                  customMicrophoneAudioTrack: customAudioStream,
                  customCameraVideoTrack: customVideoStream,
                }}
              >
                <MeetingContainer
                  meetingId={meetingId}
                  onMeetingLeave={() => setTimeout(endInterviewSafely, 500)}
                  setIsMeetingLeft={setIsMeetingLeft}
                  participantName={participantName}
                />
              </MeetingProvider>
            ) : isMeetingLeft ? (
              <LeaveScreen setIsMeetingLeft={setIsMeetingLeft} />
            ) : !isCameraSetupDone && redirectFrom != "restored" ? (
              // NEW CAMERA SCREEN
              <CameraCalibrationScreen
                onSuccess={() => {
                  setIsCameraSetupDone(true);
                }}
              />
            ) : (
              // EXISTING JOINING SCREEN (UNCHANGED)
              <JoiningScreen
                participantName={participantName}
                setParticipantName={setParticipantName}
                setMeetingId={setMeetingId}
                setToken={setToken}
                micOn={micOn}
                setMicOn={setMicOn}
                webcamOn={webcamOn}
                setWebcamOn={setWebcamOn}
                customAudioStream={customAudioStream}
                setCustomAudioStream={setCustomAudioStream}
                customVideoStream={customVideoStream}
                setCustomVideoStream={setCustomVideoStream}
                onClickStartMeeting={() => setMeetingStarted(true)}
                startMeeting={isMeetingStarted}
                setIsMeetingLeft={setIsMeetingLeft}
              />
            )}
          </>
        )}
      </MeetingAppProvider>
      {showOfflineToast && (
        <div
          style={{
            position: "fixed",
            top: 30,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#EF4444",
            color: "#fff",
            padding: "12px 18px",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 500,
            zIndex: 10000,
            boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
          }}
        >
          You are still offline. Try again once your connection is back.
        </div>
      )}
      {showAlreadyCompletedToast && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#111729",
            color: "#fff",
            padding: "30px 30px",
            borderRadius: 10,
            fontSize: "24px",
            fontWeight: 500,
            zIndex: 10000,
            boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
            padding: "30px",
          }}
        >
          You have already completed the assessment. Redirecting...
        </div>
      )}
    </NetworkErrorBoundary>
  );
}

export default Interview;
